import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import type { IIdGen } from '@/services/IdGen';
import type { ILogger } from '@/services/Logger';
import type { ISleeper } from '@/services/Sleeper';
// The ladder's shape is shared with the admin store's project writes (src/domain/backoff.ts). With
// registration the only OCC event the desk writes, three attempts is generous.
import { backoffMs, MAX_ATTEMPTS } from '@/domain/backoff';
import {
  ConflictError,
  DomainError,
  OfflineError,
  ServerError,
  UpdateRequiredError,
  ValidationError,
} from '@/domain/errors';
import type { IFrontDeskGateway } from '@/domain/gateway';
import {
  COURTS,
  courtCounts,
  isNumberTaken,
  playerNumberForCorrelation,
  registrationNumberFree,
  sessionNumbering,
  sessionNumberingEpoch,
  type Court,
  type NumberingMode,
  type PlayerNumber,
} from '@/domain/session';

// Four digits. A club that allocates off a spreadsheet is not handing out five-digit numbers, and
// the cap is what stops a thumb resting on a key producing one (docs/roster.md).
const MAX_NUMBER_DIGITS = 4;

// The front desk ViewModel. Two taps per player: register, then a court. The number comes back and
// goes on the player's arm with a marker (docs/roster.md, docs/user-journeys.md).
export class FrontDeskStore {
  private _busy = false;
  private _lastAssigned: PlayerNumber | undefined;
  private _lastError: string | undefined;
  // What is in the number input, as a STRING, because that is what an input holds and because
  // "" and "0" are different things to a half-typed field. Sanitised on the way in, so every read
  // of it is already digits.
  private _numberDraft = '';
  // Which mode the draft was typed under. The admin can flip the session between modes at any
  // point, and a number typed for a pre-allocated day is meaningless on a sequential one and
  // dangerous when the day flips back: the input is hidden while sequential, so the desk cannot
  // see or clear it, and on the way back it would arm the court buttons with a number typed for a
  // human who left an hour ago.
  //
  // A remembered EPOCH rather than a latch, because `reaction`, `autorun` and `when` are banned in
  // app code (INVARIANTS rule 3). There is nothing to subscribe to; the comparison IS the
  // subscription. The epoch and not the mode, because comparing modes cannot tell "never changed"
  // from "changed away and back", and the second one puts a number typed an hour ago back under a
  // live court button.
  private _draftEpoch = 0;

  constructor(
    private readonly gateway: IFrontDeskGateway,
    private readonly idGen: IIdGen,
    private readonly sleeper: ISleeper,
    private readonly logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  // Live per-court active counts, shared by every desk in the hall. The front desk balances the
  // courts off this number as they register (docs/roster.md), and the simulator says that matters
  // more to placement quality than the rotation algorithm does.
  get courtCounts(): Record<Court, number> {
    return courtCounts(this.gateway.sessionState);
  }

  // The four buttons, in canonical order, so the view does not reach into the domain for a list.
  get courts(): readonly Court[] {
    return COURTS;
  }

  // Where this session's numbers come from, off the fold rather than off this device: four desks in
  // one hall have to agree, and the one that disagreed would ask a human for a number the club
  // never allocated (docs/roster.md).
  get numbering(): NumberingMode {
    return sessionNumbering(this.gateway.sessionState);
  }

  // Whether this desk has to ask the human for a number before it can register them.
  get needsNumber(): boolean {
    return this.numbering === 'preassigned';
  }

  // What the input shows. Already sanitised. Empty whenever the session's mode is not the one this
  // draft was typed under, which is what stops a stale number surviving a flip.
  get numberDraft(): string {
    return this._draftEpoch === sessionNumberingEpoch(this.gateway.sessionState)
      ? this._numberDraft
      : '';
  }

  // The draft as a number, or undefined for an empty one.
  get draftNumber(): PlayerNumber | undefined {
    const draft = this.numberDraft;
    return draft === '' ? undefined : Number(draft);
  }

  // Why the typed number cannot be used, or undefined. Live off the fold, not off the last tap:
  // another desk registering 42 three seconds from now makes this appear under a thumb that has not
  // moved, which is the whole reason the count beside it is live too.
  //
  // An EMPTY draft is not a problem, it is a desk that has not typed yet. The disabled court
  // buttons are that message; an error banner for it would be shouting at somebody mid-keystroke.
  get draftProblem(): string | undefined {
    if (!this.needsNumber) return undefined;
    const number = this.draftNumber;
    if (number === undefined) return undefined;
    return isNumberTaken(this.gateway.sessionState, number) ? takenMessage(number) : undefined;
  }

  // Digits only, no leading zeros, four at most. Everything else is dropped rather than rejected:
  // the human is mid-keystroke and an input that refuses a character is an input that feels broken.
  // There is no player 0, so "0" sanitises to empty rather than to a number the fold would refuse.
  setNumberDraft = (raw: string): void => {
    this._numberDraft = raw.replace(/\D/g, '').replace(/^0+/, '').slice(0, MAX_NUMBER_DIGITS);
    this._draftEpoch = sessionNumberingEpoch(this.gateway.sessionState);
    // Editing the number makes the last failure's message stale by definition: it names a number
    // that is no longer the one on the screen. A banner still saying "41 is already registered"
    // over a field reading 42 is the feature's own mechanism lying to the desk.
    this.clearError();
  };

  // The number the desk writes on the player's arm. This desk's last registration, not the
  // aggregate's latest number: several desks register at once and the newest player in state is
  // usually somebody else's.
  get lastAssigned(): PlayerNumber | undefined {
    return this._lastAssigned;
  }

  get busy(): boolean {
    return this._busy;
  }

  // Both halves of "can this tap land". `online` is the device's own interface, which answers
  // instantly and is the only signal available before the gateway has been touched at all;
  // `gatewayReachable` is the SDK's view of whether the far side is answering, which is what a desk
  // on hall wifi with a dead gateway needs and `navigator.onLine` cannot say (docs/sync.md).
  get connectionOk(): boolean {
    return this.gateway.online && (this.gateway.gatewayReachable ?? true);
  }

  // Why the desk is closed, in the words the desk needs. The two halves have different answers:
  // "wait for the wifi" is something a human at the desk can act on, and "the gateway is not
  // answering" is something they cannot, so telling them apart is worth a sentence.
  get connectionMessage(): string | undefined {
    if (!this.gateway.online) {
      return 'Offline. Registration is closed until this device is back on the wifi.';
    }
    if (this.gateway.gatewayReachable === false) {
      return 'The server is not answering. Registration is closed; it reopens by itself.';
    }
    return undefined;
  }

  // The last registration that did not land, in words a desk can act on. A silently dropped write
  // is worse than a failed one: the desk would write a number on an arm that nobody holds.
  get lastError(): string | undefined {
    return this._lastError;
  }

  // A tap is worth making only when it can land. The view disables the court buttons on this.
  //
  // On a pre-allocated session that now includes having a usable number: a court button that can
  // only fail is a button the desk should not be able to press, and the alternative is a human
  // tapping gold and reading an error about something that is not gold.
  get canRegister(): boolean {
    if (!this.connectionOk || this._busy) return false;
    if (!this.needsNumber) return true;
    return this.draftNumber !== undefined && this.draftProblem === undefined;
  }

  // What the view binds a court button to, and the only method it calls. It never rejects: every
  // tap is `void store.tapCourt(...)`, and a rejection out of a `void` call reaches no catch, no
  // caller and no error boundary (INVARIANTS rule 17). The outcome is on `lastAssigned` or on
  // `lastError`, and one of the two is always true after this resolves.
  tapCourt = async (court: Court): Promise<void> => {
    this.clearError();
    // Captured BEFORE the write, because a successful one clears the draft and a failing one has to
    // be able to name the number in its message.
    const requested = this.needsNumber ? this.draftNumber : undefined;
    try {
      await this.register(court);
    } catch (cause) {
      const failure =
        cause instanceof DomainError ? cause : new ServerError('Registration failed.', { cause });
      this.logger.error('front desk: registration abandoned', failure.tag);
      this.showError(deskMessage(failure, requested));
    }
  };

  // Resolves with the number the reducer assigned to THIS registration, read back by correlation
  // id. Rejects with a DomainError when offline or when the write ultimately fails. Callers that
  // are not awaiting the number should use `tapCourt`.
  //
  // The optional `number` is for the callers that are not a human at a screen: the live gateway
  // suites and the load harness drive this store from a script and have no input to type into. A
  // tap from the view passes nothing and the draft is used instead.
  register = async (court: Court, number?: PlayerNumber): Promise<{ number: PlayerNumber }> => {
    // The gate, before anything is generated and before the gateway is touched. The SDK's own
    // OfflineError is the backstop; this is the UX (docs/sync.md, "Failure handling").
    if (!this.gateway.online) {
      throw new OfflineError('Cannot register: this device is offline.');
    }
    // On a sequential session the number is the reducer's to assign, so an explicit one is ignored
    // rather than honoured: honouring it would let a script put a hole in a day whose whole
    // contract is a dense run of numbers.
    const requested = this.needsNumber ? (number ?? this.draftNumber) : undefined;
    // Nothing to send. Separate from the duplicate check below and ABOVE the reentrancy guard,
    // because a desk that typed nothing has made a different mistake from a desk that tapped twice.
    if (this.needsNumber && requested === undefined) {
      throw new ValidationError('number', 'Cannot register: no number was typed.');
    }
    // One registration in flight per desk, refused before an id is generated. A second tap while
    // the first is still going is a double tap on one player, not two players: it would get its
    // own correlation id, so nothing downstream could collapse the two, and the human would walk
    // away holding two numbers. A boolean, not a counter — a counter admits the second tap and
    // only fixes when the buttons re-enable, which is the half of the bug that does not matter.
    //
    // ABOVE the duplicate check, and that order is load-bearing. Registration is OCC, so the ack
    // folds the moment the server answers and `register` resolves after that: there is a window
    // where this desk's own player is in state and the promise has not settled. A second tap in
    // that window is exactly the double tap this guard names, and checking the number first would
    // answer it with "41 is already registered, check the printout" — a number flashed at a desk
    // whose player registered perfectly well.
    if (this._busy) {
      throw new ServerError('Cannot register: this desk is already registering a player.');
    }
    // The duplicate, caught before an id is generated and before the gateway is touched. The
    // aggregate's precondition is what catches the desk that typed 42 in the same second as
    // another desk, because only the server can order those two; this catches the far commoner
    // case, which is one human reading the printout wrong, and it catches it without a round trip.
    //
    // `registrationNumberFree` rather than `isNumberTaken`: it asks the same question the
    // precondition asks, which includes "is this a player number at all". `isNumberTaken` answers
    // false for 0, for -3 and for 1.5, because no player can hold one, and waving those through
    // earns a round trip that ends in a message about a number nobody could ever have.
    if (this.needsNumber && !registrationNumberFree(this.gateway.sessionState, requested)) {
      throw new ValidationError('number', `Cannot register: number ${String(requested)} is taken.`);
    }
    // One correlation id and one idempotency key for the whole ladder. A retry that regenerates
    // either is a retry that loses its dedup and its read-back: the correlation id is how this
    // desk finds its own number, and the idempotency key is what collapses a landed-but-unacked
    // write into a duplicate rather than a second player.
    const correlationId = this.idGen.next();
    const idempotencyKey = this.idGen.next();
    this.startWrite();
    try {
      const result = await this.attempt(court, correlationId, idempotencyKey, requested);
      // Only on the way out, and only on success. A FAILED registration keeps the draft, because
      // the number is still the right one and the desk's next move is to tap again, not to read the
      // printout a second time.
      //
      // Clears the number this write CONSUMED, not whatever is in the box now. A desk that started
      // typing the next player during the round trip would otherwise watch it vanish on the ack.
      this.clearDraft(requested);
      return result;
    } finally {
      this.endWrite();
    }
  };

  private attempt = async (
    court: Court,
    correlationId: string,
    idempotencyKey: string,
    number: PlayerNumber | undefined,
  ): Promise<{ number: PlayerNumber }> => {
    for (let attempt = 1; ; attempt++) {
      try {
        // The key is OMITTED, not sent as undefined. A sequential day's payload must not carry a
        // `number` field at all: the aggregate's schema declares it optional and `t.optional`
        // rejects an explicit undefined, so a field that is merely absent-in-value would be a
        // payload the server refuses.
        await this.gateway.registerPlayer(
          number === undefined
            ? { court, correlationId, idempotencyKey }
            : { court, correlationId, idempotencyKey, number },
        );
        return this.assigned(correlationId);
      } catch (cause) {
        // Translate first, then decide. A bug inside the gateway arrives here as a TypeError and
        // must not be absorbed as if it were an OCC clash (INVARIANTS rule 17).
        const failure =
          cause instanceof DomainError ? cause : new ServerError('Registration failed.', { cause });
        // A rejection here is not evidence that nothing landed. The SDK runs a final catch-up
        // before it gives up (onlineEngine.ts folds our own event, then throws ConflictError), and
        // an ambiguous-network failure resolves the same way: the write is on the server, the ack
        // is not. So ask the read-back BEFORE believing the error. A defined number means our
        // correlation id is in the fold, which means the registration exists; telling the desk to
        // tap again would give one human two numbers (docs/sync.md, the lost-ack path).
        const landed = playerNumberForCorrelation(this.gateway.sessionState, correlationId);
        if (landed !== undefined) {
          this.logger.info(
            `front desk: registration landed despite ${failure.tag} on attempt ${attempt}`,
          );
          this.acceptNumber(landed);
          return { number: landed };
        }
        // Only a conflict is worth re-sending: the payload means the same thing wherever it
        // lands, and the aggregate has simply moved. Everything else, offline included, is a
        // failure a second identical write cannot fix.
        // A ValidationError is the aggregate's precondition refusing this number, which on this
        // event means one thing: somebody already has it. Re-sending is pointless and the desk's
        // next move is the printout, not the button.
        if (!(failure instanceof ConflictError) || attempt >= MAX_ATTEMPTS) {
          this.logger.warn(`front desk: registration failed on attempt ${attempt}`, failure.tag);
          throw failure;
        }
        this.logger.debug(`front desk: OCC clash, retrying registration (attempt ${attempt})`);
        await this.sleeper.sleep(backoffMs(idempotencyKey, attempt));
      }
    }
  };

  // The read-back. Registration is OCC, so by the time the write resolved the event had folded and
  // this desk's correlation id is in state. Never "the latest number": three other desks may have
  // landed in the same window (docs/sync.md).
  private assigned(correlationId: string): { number: PlayerNumber } {
    const number = playerNumberForCorrelation(this.gateway.sessionState, correlationId);
    if (number === undefined) {
      throw new ServerError('Registration landed but no number came back.');
    }
    this.acceptNumber(number);
    return { number };
  }

  private acceptNumber(number: PlayerNumber): void {
    this._lastAssigned = number;
    this._lastError = undefined;
  }

  private showError(message: string): void {
    this._lastError = message;
  }

  private clearError(): void {
    this._lastError = undefined;
  }

  private clearDraft(consumed: PlayerNumber | undefined): void {
    if (consumed !== undefined && this._numberDraft !== String(consumed)) return;
    this._numberDraft = '';
  }

  private startWrite(): void {
    this._busy = true;
  }

  private endWrite(): void {
    this._busy = false;
  }
}

// What the desk is told. Every message says whether a number was taken, because the next thing
// that happens is somebody writing one on an arm with a marker.
function deskMessage(failure: DomainError, requested: PlayerNumber | undefined): string {
  // A duplicate, wherever it was caught: this device's own fold, or the server's precondition after
  // another desk won the race. Both mean the same thing to the human, so both say the same thing.
  // `registerPlayer` is the only write this store makes and its only precondition is the number
  // guard, so a ValidationError out of it cannot mean anything else.
  if (failure instanceof ValidationError) {
    return requested === undefined
      ? "Type the player's number before choosing a court."
      : takenMessage(requested);
  }
  if (failure instanceof OfflineError) return 'Offline. No number was taken — wait for the wifi.';
  if (failure instanceof ConflictError) {
    return 'The session was busy. No number was taken — tap again.';
  }
  // Not "tap again": every tap on this build fails the same way until the page is reloaded onto a
  // newer one, and a desk told to tap again would stand there tapping.
  if (failure instanceof UpdateRequiredError) {
    return 'This app is out of date. No number was taken — reload the page.';
  }
  // Everything else, the reentrancy refusal included: nothing landed, and the next tap is worth
  // making.
  return 'Registration failed. No number was taken — tap again.';
}

// One sentence, one place, because the desk sees it from two directions: typed against this
// device's own fold before any write, and returned by the server when another desk won the race.
// It names the number, because the next thing that happens is a human looking at a printout.
function takenMessage(number: PlayerNumber): string {
  return `Number ${number} is already registered. No number was taken — check the printout.`;
}

export const TOKEN_FrontDeskStore = token<FrontDeskStore>('FrontDeskStore');
